/** Consumer schema for FLARE activation snapshots; availability is never inferred locally. */
export interface ActivationItem {
  target_type: 'skill' | 'capability'; target_id: string; label: string;
  registered: boolean; enabled: boolean; available: boolean; capability_ids: string[];
  icon_key?: string;
}
export interface SkillProjection {
  contract_version: string; scope: { tenant_id: string; instance_id: string }; revision: number;
  skills: ActivationItem[]; capabilities: ActivationItem[]; available_capability_ids: string[];
}
export interface SkillAPIPort {
  getSkills(input: { instanceId: string; signal?: AbortSignal }): Promise<unknown>;
  updateSkill?: (id: string, input: object, control?: { instanceId?: string; signal?: AbortSignal }) => Promise<unknown>;
}
export interface SkillAvailability {
  status: 'pending' | 'ready' | 'failed' | 'unavailable';
  projection: SkillProjection | null; error: Error | null;
}
export interface SkillReadOwner {
  isCurrent(): boolean;
  getSnapshot(): SkillAvailability;
  readFresh(): Promise<SkillProjection>;
}
export interface MessageSkillCard {
  key: string; sessionId: string; messageId: string; roundKey: string;
  skill_id: string; target_id: string; selected_capability_id: string; capability_ids: string[];
  label: string; prompt: string; description?: string;
  source: 'prompt_template_action'; interactionMode: 'composer_prefill'; messageActionSurface: 'completed_message';
}
const record = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);
const identifier = (value: unknown): value is string => typeof value === 'string' && /^\S+$/.test(value);
const identifiers = (value: unknown): value is string[] => Array.isArray(value) && value.every(identifier);
const activationItem = (value: unknown, type: ActivationItem['target_type']): value is ActivationItem => (
  record(value) && value.target_type === type && identifier(value.target_id) && typeof value.label === 'string'
  && ['registered', 'enabled', 'available'].every((key) => typeof value[key] === 'boolean')
  && (type === 'capability' || identifiers(value.capability_ids))
);

/** Validate the consumed fields and scope before a provider response enters the application. */
export const normalizeSkillProjection = (value: unknown, instanceId: string): SkillProjection => {
  if (!record(value) || value.contract_version !== 'flare.capability_activation.v1'
    || !record(value.scope) || !identifier(value.scope.tenant_id) || value.scope.instance_id !== instanceId
    || !Number.isInteger(value.revision) || Number(value.revision) < 0
    || !Array.isArray(value.skills) || !value.skills.every((item) => activationItem(item, 'skill'))
    || !Array.isArray(value.capabilities) || !value.capabilities.every((item) => activationItem(item, 'capability'))
    || !identifiers(value.available_capability_ids)) throw new TypeError('Invalid Skill activation projection');
  return structuredClone(value) as unknown as SkillProjection;
};
